import { useState, useEffect } from "react";
import api from "../../services/api";
import Dialog from "./Dialog";

interface Category {
  id: string;
  name: string;
  description?: string;
}

export default function CategoryManager() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;

    setIsProcessing(true);
    setError(null);
    try {
      await api.post("/categories", { name: newCategoryName.trim(), description: newCategoryDescription.trim() });
      setNewCategoryName("");
      setNewCategoryDescription("");
      setIsModalOpen(false);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create category");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = async (categoryId: string, newName: string, newDescription?: string) => {
    if (!newName.trim()) return;

    setIsProcessing(true);
    try {
      await api.patch(`/categories/${categoryId}`, { name: newName.trim(), description: newDescription?.trim() });
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? Courses using this category will be updated.`)) return;

    try {
      await api.delete(`/categories/${id}`);
      fetchCategories();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete category. It may be protected or in use.");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Minimalist Hero Header */}
      <div className="flex justify-between items-end pb-4 border-b border-outline-variant/10">
        <div>
          <h2 className="text-xl font-black text-on-surface tracking-tight">Category Registry</h2>
          <p className="text-[9px] font-bold text-on-surface-variant uppercase tracking-[0.2em] mt-1">Core course grouping assets</p>
        </div>
        <button 
          onClick={() => {
            setEditingCategory(null);
            setIsModalOpen(true);
            setError(null);
          }}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-black text-[9px] uppercase tracking-widest shadow-md shadow-primary/10 hover:shadow-lg active:scale-[0.98] transition-all"
        >
          <span className="material-symbols-outlined text-[14px]">add</span>
          Register Category
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {categories.map((category) => (
          <div 
            key={category.id}
            className="group bg-surface-container-low border border-outline-variant/5 rounded-[1.5rem] p-5 transition-all hover:bg-surface-container-high flex flex-row items-center gap-6 relative overflow-hidden"
          >
            {/* Visual Accent */}
            <div className="absolute left-0 top-0 bottom-0 w-1 transition-all group-hover:w-2 bg-primary/20 group-hover:bg-primary" />
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest opacity-40 italic">ID: {category.id}</span>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-on-surface/5 flex items-center justify-center text-on-surface-variant group-hover:bg-primary/10 group-hover:text-primary transition-all">
                  <span className="material-symbols-outlined text-xl">folder</span>
                </div>
                
                {editingCategory?.id === category.id ? (
                  <div className="flex flex-col gap-2 flex-1 mr-4">
                    <input
                      autoFocus
                      id={`edit-name-${category.id}`}
                      className="w-full bg-surface-container-highest px-4 py-2 rounded-lg text-base font-black text-on-surface outline-none border-2 border-primary shadow-inner"
                      defaultValue={category.name}
                      placeholder="Category Name"
                    />
                    <input
                      id={`edit-desc-${category.id}`}
                      className="w-full bg-surface-container-highest px-4 py-2 rounded-lg text-sm text-on-surface-variant outline-none border border-outline-variant/30 focus:border-primary shadow-inner"
                      defaultValue={category.description || ""}
                      placeholder="Category Description (Optional)"
                    />
                    <div className="flex gap-2 mt-1">
                      <button 
                        onClick={() => {
                          const name = (document.getElementById(`edit-name-${category.id}`) as HTMLInputElement).value;
                          const desc = (document.getElementById(`edit-desc-${category.id}`) as HTMLInputElement).value;
                          handleUpdate(category.id, name, desc);
                        }}
                        className="px-4 py-1.5 bg-primary text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-transform active:scale-95 hover:bg-primary/90"
                      >
                        Save
                      </button>
                      <button 
                        onClick={() => setEditingCategory(null)}
                        className="px-4 py-1.5 bg-surface-container-high text-on-surface-variant rounded-lg text-xs font-bold uppercase tracking-wider transition-transform active:scale-95 hover:bg-surface-container-highest"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col flex-1 mr-4">
                    <h3 className="text-base font-black text-on-surface tracking-tight group-hover:text-primary transition-colors">
                      {category.name}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-on-surface-variant mt-1 line-clamp-2 leading-snug">{category.description}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 pl-6 border-l border-outline-variant/10 h-full">
              <button 
                onClick={() => setEditingCategory(category)}
                className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/10 text-on-surface-variant hover:text-primary hover:border-primary/20 transition-all shadow-sm flex items-center gap-2 group/btn"
                title="Rename Category"
              >
                <span className="material-symbols-outlined text-xl transition-transform group-hover/btn:rotate-12">edit</span>
                <span className="text-[9px] font-black uppercase tracking-widest hidden lg:inline">Rename</span>
              </button>
              <button 
                onClick={() => handleDelete(category.id, category.name)}
                className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/10 text-on-surface-variant hover:text-red-500 hover:border-red-500/20 transition-all shadow-sm flex items-center gap-2 group/del"
                title="Delete Category"
              >
                <span className="material-symbols-outlined text-xl transition-transform group-hover/del:scale-110">delete</span>
                <span className="text-[9px] font-black uppercase tracking-widest hidden lg:inline">Delete</span>
              </button>
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <div className="py-20 text-center bg-surface-container-low border border-dashed border-outline-variant/30 rounded-[3rem] flex flex-col items-center gap-4">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 animate-pulse">folder</span>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-on-surface-variant">System Registry Empty</p>
          </div>
        )}
      </div>

      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create New Category"
      >
        <form onSubmit={handleCreate} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant ml-4">Category Name</label>
            <input
              required
              autoFocus
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g. Scaling & Operations"
              className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant ml-4">Description (Optional)</label>
            <textarea
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
              placeholder="e.g. Advanced strategies for scaling operations"
              rows={3}
              className="w-full bg-surface-container-low border border-outline-variant/30 px-6 py-4 rounded-2xl focus:ring-2 focus:ring-primary outline-none transition-all font-bold text-sm resize-none"
            />
            {error && <p className="text-[10px] font-bold text-error ml-4">{error}</p>}
          </div>

          <button
            type="submit"
            disabled={isProcessing}
            className="w-full py-4 bg-primary text-on-primary rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {isProcessing ? "Adding..." : "Register Category"}
          </button>
        </form>
      </Dialog>
    </div>
  );
}
